import React, { useEffect, useState, useMemo } from "react";
import Chart from "react-apexcharts";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";

export default function ManPowerChart() {
  const [chartData, setChasrtData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getManpowerChartData();
  }, []);

  const getManpowerChartData = () => {
    const option = {
      method: "GET",
    };
    setIsLoading(true);
    const url = apiUrl.get_site_manpower;
    callApi(url, option).then((res) => {
      setIsLoading(false);
      if (res?.status === true) {
        setChasrtData(res.data);
      } else {
        toast.error(res?.message);
      }
    });
  };

  const rainbowColors = [
    "#00C853",
    "#FFD54F",
    "#BCAAA4",
    "#BA68C8",
    "#F06292",
    "#7986CB",
    "#E57373",
    "#FFB74D",
    "#BDBDBD",
  ];

  // Memoize deterministic colors to prevent change on every re-render
  const colorsForBars = useMemo(() => {
    return chartData.map((_, index) => {
      return rainbowColors[index % rainbowColors.length];
    });
  }, [chartData]);

  // Calculate dynamic width to allow horizontal scrolling and prevent clashing
  const chartWidth = chartData.length > 0 ? Math.max(1000, chartData.length * 15) : "100%";

  // Memoize series to keep stable reference
  const series = useMemo(() => [
    {
      name: "Employees",
      data: chartData.map((item) =>
        item.siteStaffCount === 0 ? 0.5 : item.siteStaffCount ?? 0
      ),
    },
  ], [chartData]);

  // Memoize options and disable animations for instant snappiness
  const options = useMemo(() => {
    return {
      chart: {
        type: "bar",
        toolbar: { show: false },
        animations: {
          enabled: false, // Disable animations for instant resizing
        },
      },
      plotOptions: {
        bar: {
          distributed: true,
          horizontal: false,
          columnWidth: "50%",
          dataLabels: {
            position: "top", // put data labels at the top of the grid
            maxItems: 500,
          },
        },
      },
      colors: colorsForBars,
      xaxis: {
        categories: chartData.map((item) => item.name),
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        min: 0,
        labels: {
          style: {
            colors: "#666",
          },
        },
      },
      legend: {
        show: false,
      },
      grid: {
        borderColor: "#f1f1f1",
      },
      tooltip: {
        y: {
          formatter: (val, { dataPointIndex }) => {
            const site = chartData[dataPointIndex];
            return `${site.siteStaffCount}`;
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20, // push labels slightly above the bar
        style: {
          colors: ["#333"],
          fontSize: "11px",
          fontWeight: "600",
        },
        dropShadow: {
          enabled: false,
        },
        formatter: (val, { dataPointIndex }) => {
          return chartData[dataPointIndex].siteStaffCount;
        },
      },
    };
  }, [colorsForBars, chartData]);

  return (
    <div style={{ width: "100%" }}>
      <div style={{ width: "100%", overflowX: "auto", paddingBottom: "10px" }}>
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: 400 }}
          >
            <Spinner />
          </div>
        ) : (
          <div style={{ minWidth: chartWidth }}>
            <Chart options={options} series={series} type="bar" height={420} />
          </div>
        )}
      </div>
    </div>
  );
}
